import { forwardRef, type Ref, type ReactNode, type HTMLAttributes, type TableHTMLAttributes } from 'react';
import './Table.css';

export interface TableProps extends Omit<TableHTMLAttributes<HTMLTableElement>, 'border'> {
  /** Striped rows. */
  striped?: boolean;
  /** Highlight rows on hover. */
  highlightOnHover?: boolean;
  withTableBorder?: boolean;
  withColumnBorders?: boolean;
  withRowBorders?: boolean;
  /** Layout density. */
  density?: 'compact' | 'comfortable' | 'spacious';
  /** Sticky header. */
  stickyHeader?: boolean;
  stickyHeaderOffset?: number;
}

/** Themed `<table>`: base building block; pair with TanStack-Table for sorting/paging. */
const TableBase = forwardRef(function Table(
  { striped, highlightOnHover, withTableBorder, withColumnBorders, withRowBorders = true, density, stickyHeader, stickyHeaderOffset, className, children, ...rest }: TableProps,
  ref: Ref<HTMLTableElement>
): ReactNode {
  return (
    <table
      ref={ref}
      className={[
        'rr-table',
        striped && 'rr-table--striped',
        highlightOnHover && 'rr-table--hoverable',
        withTableBorder && 'rr-table--bordered',
        withColumnBorders && 'rr-table--col-bordered',
        withRowBorders && 'rr-table--row-bordered',
        density && `rr-table--density-${density}`,
        stickyHeader && 'rr-table--sticky',
        className
      ].filter(Boolean).join(' ')}
      style={stickyHeader ? { ['--rr-table-sticky-offset' as string]: `${stickyHeaderOffset ?? 0}px` } as React.CSSProperties : undefined}
      {...rest}
    >
      {children}
    </table>
  );
});

function Thead(props: HTMLAttributes<HTMLTableSectionElement>): ReactNode { return <thead {...props} />; }
function Tbody(props: HTMLAttributes<HTMLTableSectionElement>): ReactNode { return <tbody {...props} />; }
function Tfoot(props: HTMLAttributes<HTMLTableSectionElement>): ReactNode { return <tfoot {...props} />; }
function Tr(props: HTMLAttributes<HTMLTableRowElement>): ReactNode { return <tr {...props} />; }
function Th(props: HTMLAttributes<HTMLTableCellElement>): ReactNode { return <th {...props} />; }
function Td(props: HTMLAttributes<HTMLTableCellElement>): ReactNode { return <td {...props} />; }
function Caption(props: HTMLAttributes<HTMLTableCaptionElement>): ReactNode { return <caption {...props} />; }

interface TableComponent extends React.ForwardRefExoticComponent<TableProps & React.RefAttributes<HTMLTableElement>> {
  Thead: typeof Thead;
  Tbody: typeof Tbody;
  Tfoot: typeof Tfoot;
  Tr: typeof Tr;
  Th: typeof Th;
  Td: typeof Td;
  Caption: typeof Caption;
}

export const Table = TableBase as TableComponent;
Table.Thead = Thead;
Table.Tbody = Tbody;
Table.Tfoot = Tfoot;
Table.Tr = Tr;
Table.Th = Th;
Table.Td = Td;
Table.Caption = Caption;
